package com.iregained.site.components.sections.footer

import androidx.compose.runtime.*
import com.iregained.site.components.sections.header.NavLinkData
import com.iregained.site.components.sections.product.styles.KnowMoreLinkStyle
import com.iregained.site.region.activeStrings
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.ObfuscatedEmailText
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span

// UNIQUE ANIMATION: Staggered Footer Rise
@Composable
private fun Modifier.footerRise(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 30.px).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun MainFooter(data: MainFooterData = defaultFooterData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG

    val ctx = rememberPageContext()
    val currentPath = ctx.route.path.removeSuffix("/").ifEmpty { "/" }
    var isVisible by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier.fillMaxWidth().backgroundColor(Colors.White)
            .borderTop(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().custom.divider)
            .padding(top = 80.px, bottom = 20.px, leftRight = 5.percent)
    ) {
        // Observers track when the footer enters the viewport
        Div(attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).toAttrs {
            ref { element ->
                val observer = IntersectionObserver { entries, obs ->
                    entries.forEach {
                        if (it.isIntersecting) {
                            isVisible = true; obs.unobserve(element)
                        }
                    }
                }
                observer.observe(element); onDispose { observer.disconnect() }
            }
        }) {
            // TOP SECTION
            if (isDesktop) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(bottom = 60.px),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    CompanyInfoColumn(data, Modifier.footerRise(isVisible, 0))
                    data.columns.take(3).forEachIndexed { index, columnData ->
                        key(columnData.title) {
                            FooterLinkColumn(
                                columnData, currentPath, Modifier.footerRise(isVisible, 150 + (index * 150))
                            )
                        }
                    }
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(bottom = 40.px),
                    verticalArrangement = Arrangement.spacedBy(50.px)
                ) {
                    CompanyInfoColumn(data, Modifier.footerRise(isVisible, 0))

                    Row(
                        modifier = Modifier.fillMaxWidth().gap(40.px).display(DisplayStyle.Flex).flexWrap(FlexWrap.Wrap)
                    ) {
                        data.columns.forEachIndexed { index, columnData ->
                            key(columnData.title) {
                                FooterLinkColumn(
                                    columnData,
                                    currentPath,
                                    Modifier.flex(1).minWidth(150.px).footerRise(isVisible, 150 + (index * 100))
                                )
                            }
                        }
                    }
                }
            }

            // BOTTOM SECTION
            if (isDesktop) {
                Row(
                    modifier = Modifier.fillMaxWidth()
                        .borderTop(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().custom.divider)
                        .padding(top = 20.px).footerRise(isVisible, 450),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    CopyrightText(data.copyrightText)
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(30.px),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        LegalLinks(data.legalLinks, currentPath)
                        SocialIcons(data.socialLinks)
                    }
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth()
                        .borderTop(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().custom.divider)
                        .padding(top = 20.px).footerRise(isVisible, 400),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(20.px)
                ) {
                    SocialIcons(data.socialLinks)
                    LegalLinks(data.legalLinks, currentPath)
                    CopyrightText(data.copyrightText)
                }
            }
        }
    }
}

@Composable
private fun CompanyInfoColumn(data: MainFooterData, modifier: Modifier = Modifier) {
    Column(modifier = modifier.maxWidth(300.px)) {
        Link("/") { Image(src = data.logoPath, modifier = Modifier.height(40.px).margin(bottom = 15.px)) }
        Div(
            attrs = Modifier.fontSize(16.px).lineHeight(1.6).color(ColorMode.current.toSitePalette().custom.darkText)
                .margin(bottom = 20.px).toAttrs {
                    ref { htmlElement ->
                        htmlElement.innerHTML = data.companyDescription
                        onDispose {}
                    }
                })

        // RECONSTRUCTED PHONE LINK
        A(
            attrs = Modifier.color(ColorMode.current.toSitePalette().custom.darkText).opacity(0.9f)
                .textDecorationLine(TextDecorationLine.None)
                .cursor(Cursor.Pointer)
                .onClick { e ->
                    e.preventDefault()
                    val strippedPhone = data.contactPhone.replace(Regex("[^0-9+]"), "")
                    kotlinx.browser.window.location.assign("tel:$strippedPhone")
                }
                .toAttrs { attr("role", "link"); attr("tabindex", "0") }
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(8.px)) {
                Image(data.phoneIconPath, Modifier.unextractable().size(16.px))
                SpanText(
                    data.contactPhone,
                    modifier = Modifier.fontSize(16.px).fontWeight(FontWeight.Medium)
                        .color(ColorMode.current.toSitePalette().custom.darkText)
                )
            }
        }

        // RECONSTRUCTED EMAIL LINK
        A(
            attrs = Modifier.color(ColorMode.current.toSitePalette().custom.darkText).opacity(0.9f)
                .textDecorationLine(TextDecorationLine.None)
                .cursor(Cursor.Pointer)
                .onClick { e ->
                    e.preventDefault()
                    // The actual action remains functional!
                    kotlinx.browser.window.location.assign("mailto:${data.contactEmail}")
                }
                .toAttrs { attr("role", "link"); attr("tabindex", "0") }
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(8.px).margin(top = 10.px)) {
                Image(data.emailIconPath, Modifier.unextractable().size(16.px))
                ObfuscatedEmailText(
                    email = data.contactEmail,
                    modifier = Modifier.fontSize(16.px).fontWeight(FontWeight.Medium)
                        .color(ColorMode.current.toSitePalette().custom.darkText)
                )
            }
        }
    }
}

@Composable
private fun FooterLinkColumn(columnData: FooterColumnData, currentPath: String, modifier: Modifier = Modifier) {
    Column(modifier = modifier) {
        FooterTitle(columnData.title)
        Column(modifier = Modifier.gap(12.px)) {
            columnData.links.forEach { linkData ->
                key(linkData.text) {
                    // Clean base path extraction for active state
                    val linkBase = linkData.path.substringBefore("#").removeSuffix("/").ifEmpty { "/" }
                    val isActive = linkBase == currentPath

                    FooterLink(linkData.text, linkData.path, isActive)
                }
            }
        }
    }
}

@Composable
private fun CopyrightText(text: String) {
    SpanText(text = text, modifier = Modifier.fontSize(14.px).color(ColorMode.current.toSitePalette().custom.darkText))
}

@Composable
private fun LegalLinks(links: List<NavLinkData>, currentPath: String) {
    Row(
        modifier = Modifier.gap(15.px).display(DisplayStyle.Flex).flexWrap(FlexWrap.Wrap)
            .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center)
    ) {
        links.forEach { linkData ->
            key(linkData.text) {
                val isActive = linkData.path.removeSuffix("/").ifEmpty { "/" } == currentPath
                FooterLink(linkData.text, linkData.path, isActive, isSmall = true)
            }
        }
    }
}

@Composable
private fun SocialIcons(socials: List<SocialLinkData>) {
    Row(modifier = Modifier.gap(15.px), verticalAlignment = Alignment.CenterVertically) {
        socials.forEach { social ->
            key(social.iconPath) {
                A(href = social.url, attrs = Modifier.toAttrs { attr("target", "_blank") }) {
                    Image(
                        src = social.iconPath,
                        alt = social.altText,
                        modifier = Modifier.unextractable().size(26.px).borderRadius(5.px).overflow(Overflow.Hidden)
                            .cursor(Cursor.Pointer)
                    )
                }
            }
        }
    }
}

@Composable
private fun FooterTitle(text: String) {
    Column(modifier = Modifier.margin(bottom = 20.px)) {
        SpanText(
            text = text,
            modifier = Modifier.fontSize(20.px).fontWeight(FontWeight.Bold)
                .color(ColorMode.current.toSitePalette().custom.darkText).textTransform(TextTransform.Uppercase)
                .letterSpacing(1.5.px).margin(bottom = 8.px)
        )
        Box(
            modifier = Modifier.width(30.px).height(3.px)
                .backgroundColor(ColorMode.current.toSitePalette().brand.primary).borderRadius(2.px)
        )
    }
}

@Composable
private fun FooterLink(text: String, path: String, isActive: Boolean = false, isSmall: Boolean = false) {
    val palette = ColorMode.current.toSitePalette()

    // Link block with SpanText inside
    Link(
        path = path, modifier = KnowMoreLinkStyle.toModifier().textDecorationLine(TextDecorationLine.None)
    ) {
        // INJECTED: HTML Span to safely parse innerHTML without breaking the inline link flow
        Span(
            attrs = Modifier.fontSize(if (isSmall) 14.px else 18.px)
                .fontWeight(if (isActive) FontWeight.Bold else FontWeight.Normal)
                .color(if (isActive) palette.brand.primary else palette.custom.darkText).cursor(Cursor.Pointer)
                .toAttrs {
                    ref { htmlElement ->
                        htmlElement.innerHTML = text
                        onDispose {}
                    }
                })
    }
}

// ==========================================
// DATA MODELS & CMS IMAGE SPECS
// ==========================================

data class FooterColumnData(val title: String, val links: List<NavLinkData>)
data class SocialLinkData(val iconPath: String, val url: String, val altText: String)

/**
 * CMS IMAGE UPLOAD SPECS FOR FOOTER:
 * @field logoPath:
 * - Aspect Ratio: Flexible (Horizontal/Landscape).
 * - Recommended Dimensions: Max height 80px, Max width 300px.
 * - Format: SVG or transparent PNG.
 * * @field phoneIconPath / emailIconPath:
 * - Aspect Ratio: 1:1 (Square).
 * - Recommended Dimensions: 24x24px.
 * - Format: SVG.
 * * @field iconPath (inside SocialLinkData):
 * - Aspect Ratio: 1:1 (Square).
 * - Recommended Dimensions: 64x64px.
 * - Format: SVG or transparent PNG.
 */
data class MainFooterData(
    val logoPath: String,
    val companyDescription: String,
    val phoneIconPath: String,
    val contactPhone: String,
    val emailIconPath: String,
    val contactEmail: String,
    val copyrightText: String,
    val columns: List<FooterColumnData>,
    val legalLinks: List<NavLinkData>,
    val socialLinks: List<SocialLinkData>
)

val defaultFooterData = MainFooterData(
    logoPath = Res.Images.Common.LOGO,
    companyDescription = "Empowering stroke and brain injury survivors regain hand function and independence through innovative neuro-rehabilitation technology.",
    phoneIconPath = Res.Icons.CALL,
    contactPhone = activeStrings.supportPhone,
    emailIconPath = Res.Icons.SEND_EMAIL,
    contactEmail = activeStrings.emailText,
    copyrightText = "© 2026 ${Res.Strings.APP_NAME} Inc. All rights reserved.",
    columns = listOf(
        FooterColumnData(
            "Platform", listOf(
                NavLinkData("MyHand<sup>®</sup> System", "/product"),
                NavLinkData("For Patients", "/patients"),
                NavLinkData("For Clinicians", "/clinicians"),
                NavLinkData("Success Stories", "/testimonials"),
                NavLinkData("Resources", "/resources")
            )
        ), FooterColumnData(
            "Resources", listOf(
//                NavLinkData("Career Opportunities", "/about#about-careers-section"),
//                NavLinkData("Clinic Locator", "/contact"),
                NavLinkData("Distributors", "/distributors"),
                NavLinkData("Financing Options", "/financing"),
//                NavLinkData("Resources", "/resources")
            )
        ), FooterColumnData(
            "Company", listOf(
                NavLinkData("About Us", "/about"),
                NavLinkData("News & Press", "/news"),
                NavLinkData("Contact Us", "/contact")
            )
        )
    ),
    legalLinks = listOf(
        NavLinkData("Privacy & Cookies", "/privacy-policy"), NavLinkData("Terms of Service", "/terms-of-service")
    ),
    socialLinks = listOf(
        SocialLinkData(Res.Icons.FACEBOOK, "https://www.facebook.com/Iregainedinc/", "Facebook"),
        SocialLinkData(Res.Icons.INSTAGRAM, "https://www.instagram.com/iregained_inc/", "Instagram"),
        SocialLinkData(Res.Icons.LINKEDIN, "https://www.linkedin.com/company/iregained-inc/", "LinkedIn"),
        SocialLinkData(Res.Icons.TWITTER, "https://x.com/IRegainedInc", "Twitter")
    )
)
